import PropTypes from 'prop-types'
import { useEffect, useRef, useState } from 'react'
import hotelApi from 'services/api/hotel'

import AddHotelGroupModal from '../../../components/ReusableComponents/ManageHotel/AddHotelGroup/index'
import Dropdown from '../../../components/ReusableComponents/ManageHotel/Dropdown/index'
import FormGroup from '../../../components/ReusableComponents/ManageHotel/FormGroup/index'
import Toggle from '../../../components/ReusableComponents/Toggle/index'
import { parentChainNames, subChains } from '../../../main/hotelBrandsAndChains'
import { useChainOptions } from '../../../utils/manageHotel/chainsHelper'
import ChainLogo from '../ChainLogo/index'
import styles from './ParentAndChain.module.scss'

function getChainFromSubchain(subchain) {
  return Object.keys(subChains).find(chain => subChains[chain].includes(subchain))
}

const ParentAndChain = ({
  formData: { parentChainCode, chainCode, merchantId, urlKey },
  handleFormChange,
  setFormData,
  disabled,
  handleValidated,
  done
}) => {
  const firstInputRef = useRef()
  useEffect(() => {
    if (!disabled && firstInputRef && firstInputRef.current) {
      firstInputRef.current.focus()
    }
  }, [disabled])

  const [brand, setBrand] = useState()

  function handleParentChange(e) {
    setParent(e.target.value)
    setFormData({ chainCode: null })
    handleFormChange(e)
  }

  const [chains, setParent] = useChainOptions()

  useEffect(() => {
    setParent(parentChainCode)
  }, [parentChainCode, setParent])

  useEffect(() => {
    if (parentChainCode === 'independent' && chainCode !== 'i') {
      handleValidated(true)
      setFormData({ chainCode: 'i' })
      return
    }

    if (parentChainCode) {
      handleValidated(!!chainCode)
    }

    if (parentChainCode !== 'independent' && chainCode === 'i') {
      setFormData({ chainCode: null })
    }
  }, [parentChainCode, chainCode, handleValidated, setFormData])

  const [hasOwner, setHasOwner] = useState(merchantId !== -100)

  const [isHotelGroupsLoading, setIsHotelGroupsLoading] = useState(true)

  const [hotelGroups, setHotelGroups] = useState()

  useEffect(() => {
    setIsHotelGroupsLoading(true)
    hotelApi
      .getHotelGroups()
      .then(data => {
        setHotelGroups({
          ...data.reduce(
            (prev, current) => ({
              ...prev,
              [current.id]:
                current.name === 'Independently Managed Hotels'
                  ? '- Independently Managed'
                  : current.name
            }),
            {}
          ),
          new: '- Add new management company'
        })
      })
      .finally(() => setIsHotelGroupsLoading(false))
  }, [])

  function handleOwnerGroupChange(e) {
    if (e.target.value === 'new') {
      setShowAddOwnerDialog(true)
    } else {
      handleFormChange(e)
    }
  }

  const [showAddOwnerDialog, setShowAddOwnerDialog] = useState()

  useEffect(() => {
    if (!hasOwner) {
      setFormData({ merchantId: -100 })
    }
  }, [hasOwner, setFormData])

  return (
    <FormGroup done={done} title="Parent and chain" disabled={disabled}>
      <div className={styles.container}>
        <div>
          <Toggle
            label="Has owner management group"
            isGray
            className={styles.ownerGroupToggle}
            handleChange={e => setHasOwner(e.target.checked)}
            checked={hasOwner}
            forwardedRef={firstInputRef}
          />
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <Dropdown
            forwardedRef={firstInputRef}
            options={parentChainNames}
            label="Brand"
            defaultVal="Select parent"
            value={parentChainCode}
            name="parentChainCode"
            onChange={handleParentChange}
            valid={parentChainCode === null ? '' : !!parentChainCode}
          />

          {parentChainCode !== 'independent' && (
            <Dropdown
              label="Chain"
              disabled={!parentChainCode}
              defaultVal={parentChainCode ? 'Select chain' : 'Select parent first'}
              options={{ ...chains }}
              name="chainCode"
              value={
                Object.keys(chains).includes(chainCode)
                  ? chainCode
                  : getChainFromSubchain(brand)
              }
              valid={!!chainCode}
              onChange={e => setFormData({ chainCode: e.target.value })}
            />
          )}
        </div>
        {parentChainCode && parentChainCode !== 'independent' && (
          <ChainLogo
            className={styles.chainLogo}
            parentChainCode={parentChainCode}
            brand={brand}
            setBrand={setBrand}
            setChainCode={chainCode => setFormData({ chainCode })}
            chainCode={chainCode}
            hotelIdentifier={urlKey}
          />
        )}
        {hasOwner && (
          <>
            <br />
            <Dropdown
              options={hotelGroups}
              name="merchantId"
              value={merchantId}
              onChange={handleOwnerGroupChange}
              label="Owner group"
              disabled={isHotelGroupsLoading}
              defaultVal={isHotelGroupsLoading ? 'Loading' : 'Select owner group'}
            />
          </>
        )}
        {showAddOwnerDialog && (
          <AddHotelGroupModal
            switchToHotel={merchantId => setFormData({ merchantId })}
            addHotelToList={data => setHotelGroups(g => ({ ...g, [data.id]: data.name }))}
            closeModal={() => setShowAddOwnerDialog(false)}
          />
        )}
      </div>
    </FormGroup>
  )
}

ParentAndChain.propTypes = {
  formData: PropTypes.object.isRequired,
  handleFormChange: PropTypes.func.isRequired,
  setFormData: PropTypes.func.isRequired,
  handleValidated: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  done: PropTypes.bool
}

export default ParentAndChain
